// Platform Configuration
export const PLATFORM_CONFIG = {
  // Platform fee percentage (2% = 0.02)
  PLATFORM_FEE_PERCENTAGE: 0.02,
  
  // Platform fee display name
  PLATFORM_FEE_LABEL: "Platform Fee",
  
  // Platform fee description
  PLATFORM_FEE_DESCRIPTION: "Platform service fee",
  
  // Minimum platform fee amount (in cents)
  MIN_PLATFORM_FEE_CENTS: 50, // $0.50
  
  // Maximum platform fee amount (in cents) - 0 means no limit
  MAX_PLATFORM_FEE_CENTS: 0,
} as const;

// Helper functions for platform fee calculations
export const PlatformFeeUtils = {
  /**
   * Calculate platform fee for a given amount
   * @param amount - The base amount to calculate fee for
   * @returns The platform fee amount
   */
  calculatePlatformFee: (amount: number): number => {
    return amount * PLATFORM_CONFIG.PLATFORM_FEE_PERCENTAGE;
  },

  /**
   * Calculate total amount including platform fee
   * @param amount - The base amount
   * @returns The total amount including platform fee
   */
  calculateTotalWithFee: (amount: number): number => {
    return amount + PlatformFeeUtils.calculatePlatformFee(amount);
  },

  /**
   * Get platform fee percentage as a display string
   * @returns Formatted percentage string (e.g., "2%")
   */
  getPlatformFeePercentageDisplay: (): string => {
    return `${(PLATFORM_CONFIG.PLATFORM_FEE_PERCENTAGE * 100).toFixed(0)}%`;
  },

  /**
   * Format platform fee for display
   * @param amount - The base amount
   * @param currency - The currency symbol
   * @returns Formatted fee string (e.g., "$2.00")
   */
  formatPlatformFee: (amount: number, currency: string = "$"): string => {
    const fee = PlatformFeeUtils.calculatePlatformFee(amount);
    return `${currency}${fee.toFixed(2)}`;
  },

  /**
   * Format total amount with fee for display
   * @param amount - The base amount
   * @param currency - The currency symbol
   * @returns Formatted total string (e.g., "$102.00")
   */
  formatTotalWithFee: (amount: number, currency: string = "$"): string => {
    const total = PlatformFeeUtils.calculateTotalWithFee(amount);
    return `${currency}${total.toFixed(2)}`;
  }
};
